import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface LegalLayoutProps {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}

/**
 * Shared shell for /privacy and /terms. Plain-text-readable typography,
 * a "last updated" date, and a clear path back to the canvas. Treats
 * these pages as parent-readable documents — no clever animations.
 */
export function LegalLayout({ title, lastUpdated, children }: LegalLayoutProps): JSX.Element {
  return (
    <div className="min-h-dvh bg-amber-50 px-4 py-8 sm:py-12">
      <article className="mx-auto max-w-2xl rounded-3xl bg-white p-6 shadow-md sm:p-10">
        <header className="mb-6">
          <h1 className="font-bold text-3xl text-slate-900 sm:text-4xl">{title}</h1>
          <p className="mt-2 text-slate-500 text-sm">Last updated: {lastUpdated}</p>
        </header>
        <div className="prose prose-slate max-w-none">{children}</div>
        <footer className="mt-8 flex items-center justify-between text-sm">
          <Link to="/" className="text-amber-600 underline-offset-2 hover:underline">
            ← Back to canvas
          </Link>
          <span className="text-slate-400">
            <Link to="/privacy" className="hover:underline">
              Privacy
            </Link>
            {' · '}
            <Link to="/terms" className="hover:underline">
              Terms
            </Link>
          </span>
        </footer>
      </article>
    </div>
  );
}
